import { FormInput, YesNoRadioButton, spreadAndtransformDefaultValues } from '@common/components/forms';
import { useApp, useIsMounted } from '@common/hooks';
import { IntakePetModel } from '@common/models';
import { IntakePetStoreContextWrapper } from '@customer/contexts';
import { isEmpty } from 'lodash';
import { observer } from 'mobx-react';
import React, { useCallback, useContext, useEffect } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { trackPromise } from 'react-promise-tracker';
import { useNavigate } from 'react-router-dom';
import { IntakeQuestion, PetPhotoUpload } from '.';
import { DateTimeUtils } from '@common/utils/datetimeUtils';

export const IntakePetDetail = observer(() => {
    const store = useContext(IntakePetStoreContextWrapper.get());
    const { configuration } = useApp();
    const isMounted = useIsMounted();

    const formMethods = useForm<IntakePetModel>({
        shouldFocusError: true,
        defaultValues: spreadAndtransformDefaultValues(store.selectedPet)
    });

    const { handleSubmit, reset, control, register } = formMethods;
    const navigate = useNavigate();

    useEffect(() => {
        if (!isEmpty(store.selectedPet)) {
            reset(spreadAndtransformDefaultValues(store.selectedPet));
        }
    }, [store.selectedPet, reset]);


    const savePetPhoto = useCallback((photoToSave: File): Promise<void> => {
        return trackPromise(new Promise((resolve, reject) => {
            store.savePetPhoto(photoToSave).then(() => {
                if (!isMounted()) {
                    reject('unmounted');
                    return;
                }
                resolve();
            })
        }));
    }, [store, isMounted]);

    return (
        <>
            <FormProvider {...formMethods}>
                <Form>
                    {
                        // Pet Details
                        store.selectedSectionIndex === -1 ?
                            <>
                                <Row>
                                    <Col xs={12}>
                                        <p className="sub-header">Review and complete your pet&apos;s information.</p>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={12} className="text-center">
                                        <PetPhotoUpload isReadOnly={store.intakeStore.intake.isReadOnly} intakePet={store.selectedPet} onPhotoChanged={savePetPhoto} />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={6}>
                                        <FormInput
                                            tabIndex={1}
                                            label=""
                                            controlId="name"
                                            placeholder='Pet Name'
                                            fieldName="name"
                                            control={control}
                                            disabled={store.intakeStore.isReadOnly}
                                            highlightDirty
                                            isRequired
                                        />
                                    </Col>
                                    <Col xs={6}>
                                        <Form.Select className="form-control"
                                            {...register('speciesId')}
                                            disabled={true}>
                                            <option value="">Species</option>
                                            {configuration.species.map(species => <option key={`species_${species.speciesId}`} value={species.speciesId}>{species.name}</option>)}
                                        </Form.Select>
                                        {/* <FormSelect
                                            tabIndex={2}
                                            label=""
                                            fieldName="speciesId"
                                            control={control}
                                            isRequired={true}
                                            options={configuration.species}
                                            getOptionLabel={(s) => s.name}
                                            getOptionValue={(s) => s.speciesId.toString()}
                                            mapOptionToValue={(opt) => opt?.speciesId}
                                            mapValueToOption={(speciesId) => _.find(configuration.species, { speciesId: speciesId })}
                                            onOptionChanged={(speciesId) => {
                                                _.find(configuration.species, {
                                                    speciesId: speciesId
                                                })
                                            }}
                                        /> */}
                                    </Col>
                                </Row>
                                <Row className="mt-3">
                                    <Col xs={12}>
                                        <Form.Select className="form-control"
                                            {...register('breedId')} >
                                            <option value="">Breed</option>
                                            {configuration.breeds.filter(b => b.speciesId === store.selectedPet.speciesId).map(breed => <option key={`breed_${breed.breedId}`} value={breed.breedId}>{breed.name}</option>)}
                                        </Form.Select>
                                        {/* <FormSelect
                                            tabIndex={3}
                                            label=""
                                            fieldName="breedId"
                                            control={control}
                                            isRequired={true}
                                            options={configuration.breeds}
                                            getOptionLabel={(s) => s.name}
                                            getOptionValue={(s) => s.breedId.toString()}
                                            mapOptionToValue={(opt) => opt?.breedId}
                                            mapValueToOption={(breedId) => _.find(configuration.breeds, { breedId: breedId })}
                                            onOptionChanged={(breedId) => {
                                                _.find(configuration.breeds, {
                                                    breedId: breedId
                                                })
                                            }}
                                        /> */}
                                    </Col>
                                </Row>
                                <Row className="mt-3">
                                    <Col xs={2}>
                                        <label className="question" htmlFor="mixedBreed">Mixed?</label>
                                    </Col>
                                    <Col xs={10}>
                                        <Controller
                                            control={control}
                                            name={`isMixedBreed`}
                                            render={({ field: { onChange, value, name } }) =>
                                                <Form.Check
                                                    id={`isMixedBreed_yes`}
                                                    inline
                                                    name={name}
                                                    label={"Yes"}
                                                    type="radio"
                                                    checked={value === true}
                                                    onChange={(e) => {
                                                        if (e.target.checked)
                                                            onChange(true);
                                                    }}
                                                />
                                            }
                                        />
                                        <Controller
                                            control={control}
                                            name={`isMixedBreed`}
                                            render={({ field: { onChange, value, name } }) =>
                                                <Form.Check
                                                    id={`isMixedBreed_no`}
                                                    inline
                                                    name={name}
                                                    label={"No"}
                                                    type="radio"
                                                    checked={value === false}
                                                    onChange={(e) => {
                                                        if (e.target.checked)
                                                            onChange(false);
                                                    }}
                                                />}
                                        />

                                    </Col>
                                </Row>
                                <Row className="mt-3">
                                    <Col xs={6}>
                                        <Form.Select className="form-control"
                                            {...register('petColorId')} >
                                            <option value="">Color</option>
                                            {configuration.petColors.map(color => <option key={`breed_${color.petColorId}`} value={color.petColorId}>{color.name}</option>)}
                                        </Form.Select>
                                        {/* <FormSelect
                                            tabIndex={5}
                                            label=""
                                            fieldName="petColorId"
                                            control={control}
                                            options={configuration.petColors}
                                            getOptionLabel={(s) => s.name}
                                            getOptionValue={(s) => s.petColorId.toString()}
                                            mapOptionToValue={(opt) => opt?.petColorId}
                                            mapValueToOption={(petColorId) => _.find(configuration.petColors, { petColorId: petColorId })}
                                            onOptionChanged={(petColorId) => {
                                                _.find(configuration.petColors, {
                                                    petColorId: petColorId
                                                })
                                            }}
                                        /> */}
                                    </Col>
                                    <Col xs={6}>
                                        <Form.Select className="form-control"
                                            {...register('genderId')} >
                                            <option value="">Gender</option>
                                            {configuration.genders.map(gender => <option key={`gender_${gender.genderId}`} value={gender.genderId}>{gender.name}</option>)}
                                        </Form.Select>
                                        {/* <FormSelect
                                            className={"form-control"}
                                            tabIndex={6}
                                            label=""
                                            fieldName="genderId"
                                            control={control}
                                            options={configuration.genders}
                                            getOptionLabel={(s) => s.name}
                                            getOptionValue={(s) => s.genderId.toString()}
                                            mapOptionToValue={(opt) => opt?.genderId}
                                            mapValueToOption={(genderId) => _.find(configuration.genders, { genderId: genderId })}
                                            onOptionChanged={(genderId) => {
                                                _.find(configuration.genders, {
                                                    genderId: genderId
                                                })
                                            }}
                                        /> */}
                                    </Col>
                                </Row>
                                <Row className="mt-3">
                                    <Col xs={6}>
                                        <Form.Group controlId="dob">
                                            <Form.Label className="question px-0 mb-0">DOB</Form.Label>
                                            <Controller
                                                control={control}
                                                name="birthDate"
                                                render={({ field: { onChange, value } }) => (
                                                    <DatePicker
                                                        className="form-control"
                                                        placeholderText="Select date"
                                                        onChange={(d) => onChange(d)}
                                                        selected={value}
                                                    />
                                                )}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                            </>
                            : null
                    }

                    {/* Sections */}
                    {
                        store.selectedSectionIndex >= 0 ?
                            <Row key={`section_${store.selectedSectionIndex}`}>
                                <Col xs={12}>
                                    <p className="sub-header">{store.selectedPet.sections[store.selectedSectionIndex].name}</p>
                                </Col>
                                {
                                    store.selectedPet.sections[store.selectedSectionIndex].questions.map((q, questionIdx) => (
                                        <IntakeQuestion
                                            key={`question_${questionIdx}${q.questionId}`}
                                            question={q}
                                            questionIndex={questionIdx}
                                            selectedSectionIndex={store.selectedSectionIndex}
                                            updateSelectedQuestionListValue={store.updateSelectedQuestionListValue}
                                        />
                                    ))
                                }
                            </Row>
                            : null
                    }


                    {/* Form Controls */}
                    {
                        store.selectedSectionIndex !== (store.progressSections.length - 2) ?
                            <Row className="mt-3">
                                <Col xs={12}>
                                    <div className="d-grid gap-2 mb-2">
                                        <Button onClick={() => {
                                            store.nextSection()
                                        }} className="text-uppercase flex-fill">
                                            Next
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                            : null
                    }

                    {
                        store.selectedSectionIndex === (store.progressSections.length - 2) ?
                            <Row className="mt-3">
                                <Col xs={12}>
                                    <div className="d-grid gap-2 mb-2">
                                        <Button type="submit"
                                            onClick={handleSubmit(
                                                (m: any) => store.updateIntakePet(DateTimeUtils.objToDateTime(m)) // TODO fix ERROR: Type 'IntakePetModel' is not assignable to type 'IIntakePetModel'.
                                                    .then(() => { navigate(`/intake/${store.intakeStore.intakePublicId}`) }))}
                                            className="text-uppercase flex-fill">
                                            {store.intakeStore.isReadOnly ? 'Finish' : 'Save and Finish'}
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                            : null
                    }
                </Form>
            </FormProvider>
        </>
    )
});
